import { Component } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../services/alerts.service';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { LoginService } from '../../login/services/login.service';
import { SsoSecurityService } from '../../login/services/sso-security.service';
import { RoutingService } from '../../services/routing.service';

/**
 * Component providing user login functionality,
 * presented as a modal.
 */
@Component({
	selector: 'login-modal',
	templateUrl: './login-modal.component.html',
	styleUrl: './login-modal.component.css'
})
export class LoginModalComponent implements OnInit, OnDestroy {
	
	private userEventSubscription!: Subscription;
	private routingEventSubscription!: Subscription;

	LOCALIZATION: any;

	formMode: string = 'login';
	input = {
		username: '',
		email: '',
		password: '',
		newpass: '',
		newpass2: '',
		firstname: '',
		lastname: '',
		captchaAnswer: ''
	};

	popoverHTML!: SafeHtml;
	validPass: boolean = false;

	captchaType: string = "IMAGE";
	captchaSource: string = `/aw-server/rest/register/user/captcha?type=${this.captchaType}`;
	captchaAudioSource: string = "";

	showPolicy: boolean = false;
	publicEnabled: boolean = false;
	registerEnabled: boolean = false;
	ldapEnabled: boolean = false;
	passwordEnabled: boolean = false;
	azureEnabled: boolean = false;
	adfsEnabled: boolean = false;
	idplist: any[] = [];
    isHideBasicAuthentication: boolean = false;
    
	constructor(
		private sanitizer: DomSanitizer,
		private route: ActivatedRoute,
		private router: Router,
		private ngbModalService: NgbActiveModal,
		private alertsService: AlertsService,
		private configService: ConfigService,
		private userService: UserService,
		private loginService: LoginService,
		private ssoService: SsoSecurityService,
		private routingService: RoutingService
	) {
		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if (event === 'localizationLoaded') {
				this.LOCALIZATION = this.userService.LOCALIZATION; 
			}
		});
	}

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		
		if (this.userService?.user && !this.isAnonymusUser()) {
			// logout current user
			this.userService.logout(false, false).then(
				(response: any) => {
					if (this.configService.getConfig().auth?.public?.autoEnabled) {
						this.userService.attemptPublicUserLogin().then(() => {
							this.userService.processToken().then(() => {
								//this.userService.runBroadcastEvent('userPrefsUpdate');
							});
							this.initProps();
						}).catch((error: any) => {
							const config = this.configService.getConfig();
							if (config.auth && config.auth?.public) {
								config.auth.public.autoEnabled = false;
							}
							this.userService.resetUserToAnonymous();
							this.userService.runBroadcastEvent('userPrefsUpdate');
							this.initProps();
						});
					} else {
						this.userService.resetUserToAnonymous();
						this.userService.runBroadcastEvent('userPrefsUpdate');
						this.initProps();
					}
				},
				(err: any) => {
					this.userService.resetUserToAnonymous();
					this.userService.runBroadcastEvent('userPrefsUpdate');
					this.initProps();
				}
			);
		} else {
			this.initProps();
		}
		
		this.routingEventSubscription = this.routingService.routingEvent.subscribe(event => {
			if (event.eventType === 'navigationEnded') {
				this.updateFormState();
			}
		});		
	}

    private initProps(): void {
		this.registerEnabled = (this.configService.getConfig().auth?.register || this.configService.getConfig().auth?.public?.register) && this.configService.system.register;
		this.publicEnabled = this.configService.getConfig().auth?.public?.autoEnabled ?? false;
		this.isAzureEnabled();
		this.getIDPSelection();
		this.isPasswordEnabled();
		this.isLdapEnabled();
		this.isHideBasicAuthentication = this.configService.getConfig().auth?.isHideBasicAuthentication;
		
		const params = { ...this.routingService.getQueryParams() };
		if (params['m'] && 'register' === params['m']) {
			this.changeFormMode('register');
		}
		
		this.refreshChallenge();	
	}
	
	private isAnonymusUser(): boolean {
		return this.configService.getConfig().auth?.public?.autoEnabled && this.userService?.isAnonymusUser();
	}
	
	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}
		
		if (this.routingEventSubscription) {
			this.routingEventSubscription.unsubscribe();
		}		
	}
	
	private updateFormState(): void {
		const params = { ...this.routingService.getQueryParams() };
		if (params['m']) {
			let mode = params['m'];
			this.changeFormMode(mode);
		}		
	}
	
	changeFormMode(formMode: string, event?: Event): void {
		if (event) event.preventDefault();

		this.formMode = formMode;
		this.clearFields();
		if (formMode === "register") {
			this.initRegistration();
		}
	}

	private initRegistration(): void {
		this.createValidationResponse();
	}

	/** 
	 * Logs in the user.
	 */
	login(): void {
		if (!this.input.username && !this.input.password) {
			this.alertsService.error({
				title: this.LOCALIZATION.loginFailed,
				message: this.LOCALIZATION.loginUsernamePasswordPrompt,
				delay: 5000,
				startTop: 20,
				nobroadcast: true
			});
			return;
		}

		this.userService.logout().then(
			(response: any) => {
				this.userService.login(this.input.username, this.input.password, false).then(
					(response: any) => {
						this.proceed('logged-in');
					},
					(err: any) => {
						let errMsg = this.LOCALIZATION.loginInvalidCreds;
						if (err?.status === 401) {
							errMsg = this.LOCALIZATION.loginUnauthorized;
						}						
						this.alertsService.error({
							title: this.LOCALIZATION.loginFailed,
							message: errMsg,
							delay: 5000,
							nobroadcast: true
						});
						
						this.userService.attemptPublicUserLogin();
					}
				);
			},
			(err: any) => {
				console.error("Logout failed", err);
			}
		);
	}

	/** 
	 * Presents a form for new user registration.
	 */
	register(): void {
		if (!this.input.username || !this.input.email || !this.input.newpass ||
			!this.input.newpass2 || !this.input.firstname ||
			!this.input.lastname || !this.input.captchaAnswer) {
			this.alertsService.error({
				message: this.LOCALIZATION.fillAllFields,
				delay: 5000
			});
			return;
		}

		if (this.input.newpass != this.input.newpass2) {
			this.alertsService.error({
				title: this.LOCALIZATION.loginRegistrationFail,
				message: this.LOCALIZATION.loginPasswordsNoMatch,
				delay: 5000
			});
			return;
		}

		let validationErrors = this.validatePassword();
		if (!this.validPass) {
			this.alertsService.error({
				title: this.LOCALIZATION.loginRegistrationFail,
				message: this.LOCALIZATION.loginPasswordNoValidation + "\n" + validationErrors,
				delay: 5000
			});
			return;
		}

		let data = {
			username: this.loginService.sanitizeInput(this.input.username),
			email: this.loginService.sanitizeInput(this.input.email),
			password: this.loginService.sanitizeInput(this.input.newpass),
			firstName: this.loginService.sanitizeInput(this.input.firstname),
			lastName: this.loginService.sanitizeInput(this.input.lastname)
		};

		this.userService.registerUser(data, this.input.captchaAnswer).then(
			(response: any) => {
				this.alertsService.success({
					title: this.LOCALIZATION.loginRegistrationSuccess,
					message: "Success, now logging in new user...",
					delay: 1500
				});
				this.userService.login(data.username, data.password, false).then(
					(response: any) => {
						this.input.newpass = "";
						this.input.newpass2 = "";
						this.proceed('registered-user');
					},
					(err: any) => {
						this.alertsService.error({
							title: this.LOCALIZATION.loginFailed,
							message: "Failed to log in the new user.",
							delay: 5000
						});
					}
				);
			},
			(err: any) => {
				console.error("Login registration failed", err);
				this.alertsService.error({
					title: this.LOCALIZATION.loginRegistrationFail,
					message: "Registration could not be completed.",
					delay: 6000
				});
			}
		);
	}

	/** 
	 * Presents a form for username recovery.
	 */
	recoverUsername(): void {
		if (!this.input.email || !this.input.firstname || !this.input.lastname || !this.input.captchaAnswer) {
			this.alertsService.error({
				message: this.LOCALIZATION.fillAllFields,
				delay: 5000
			});
			return;
		}

		this.userService.recoverUsername(this.input.email, this.input.firstname, this.input.lastname, this.input.captchaAnswer).then(
			(response: any) => {
				this.alertsService.success({
					message: this.LOCALIZATION.recoveryEmail,
					delay: 5000
				});
				this.changeFormMode('login');
			},
			(err: any) => {
				console.error("Failed to recover username", err);
				this.alertsService.error({
					title: "Request Failed",
					message: err,
					delay: 5000
				});
			}
		);
	}

	/** 
	 * Presents a form for password recovery.
	 */
	recoverPassword(): void {
		if (!this.input.username || !this.input.email || !this.input.captchaAnswer) {
			this.alertsService.error({
				message: this.LOCALIZATION.fillAllFields,
				delay: 5000
			});
			return;
		}
		
		let currentPath = this.route.snapshot.url.map(segment => segment.path).join('/');
		const resetUrlTree = this.router.createUrlTree(['/reset']);
        const resetUrl = this.router.serializeUrl(resetUrlTree);
        const path = `${currentPath}${resetUrl}`;

		this.userService.recoverPassword(this.input.username, this.input.email, path, this.input.captchaAnswer).then(
			(response: any) => {
				this.alertsService.success({
					message: this.LOCALIZATION.recoveryEmail,
					delay: 5000
				});
				this.changeFormMode('login');
			},
			(err: any) => {
				console.error("Failed to recover password", err);
				this.alertsService.error({
					title: "Request Failed",
					message: err,
					delay: 5000
				});
			}
		);
	}

	ok(): void {
		switch (this.formMode) {
			case "login":
				this.login();
				break;
			case "register":
				this.register();
				break;
			case "passwordRecovery":
				this.recoverPassword();
				break;
			case "userRecovery":
				this.recoverUsername();
				break;
		}
	}

	cancel(): void {
		if (this.publicEnabled) {
			this.proceed('cancel');
		}
	}

	private createValidationResponse(): void {
		let html = this.loginService.getValidationResponse(this.validPass);
		this.popoverHTML = this.sanitizer.bypassSecurityTrustHtml(html);
	}

	validatePassword(): string {
		this.validPass = this.loginService.passwordValidate(this.input.newpass);
		this.createValidationResponse();
		return this.loginService.getValidationErrors();
	}

	/** 
	 * Presents a new captcha challenge.
	 */
	refreshChallenge(): void {
		if (this.captchaType == "IMAGE") {
			this.captchaSource = this.captchaSource + "&decache=" + Math.random();
		} else {
			this.captchaAudioSource = this.captchaAudioSource + "&decache=" + Math.random();
		}
	}

	/**
	 * Presents an image-based captcha challenge.
	 */
	imageChallenge(): void {
		this.captchaType = "IMAGE";
		this.captchaAudioSource = "";
	}

	/**
	 * Presents an audio-based captcha challenge.
	 */
	audioChallenge(): void {
		this.captchaType = "AUDIO";
		this.captchaAudioSource = `/aw-server/rest/register/user/captcha?type=${this.captchaType}`;
	}

	clearFields(): void {
		this.input.username = "";
		this.input.email = "";
		this.input.password = "";
		this.input.newpass = "";
		this.input.newpass2 = "";
		this.input.firstname = "";
		this.input.lastname = "";
		this.input.captchaAnswer = "";
	}

	getIDPSelection(): void {
		this.ssoService.getIDPSelection().then(
			(list: any) => {
				this.idplist = [];

				Object.entries(list).forEach(([key, val]) => {
					let tmp = { name: val, url: key };
					this.idplist.push(tmp);
				});

				if (this.idplist.length > 0) {
					this.adfsEnabled = true;
				} else {
					this.adfsEnabled = false;
				}
			},
			(err: any) => {
				console.error("Failed to get IDP selection", err);
				this.adfsEnabled = false;
			}
		);
	}

	isAzureEnabled(): void {
		this.ssoService.getAzureEnabled().then(
			(response: any) => {
				this.azureEnabled = response;
			},
			(err: any) => {
				console.error("Failed to get Azure enabled status", err);
			}
		);
	}

	isPasswordEnabled(): void {
		this.ssoService.getPasswordLoginEnabled().then(
			(response: boolean) => {
				this.passwordEnabled = response;
			},
			(err: any) => {
				console.error("Failed to get password login enabled status", err);
			}
		);
	}

	isLdapEnabled(): void {
		this.ssoService.getLdapLoginEnabled().then(
			(response: any) => {
				this.ldapEnabled = response;
			},
			(err: any) => {
				console.error("Failed to get Ldap enabled status", err);
			}
		);
	}

	/**
	 * Closes the modal, returning a result of ok and payload.
	 */
	proceed(action: string): void {
		let caller = {
			callerName: 'loginModal',
			action: action
		};

		this.ngbModalService.close({
			result: { answer: 'ok', caller: caller },
			timestamp: new Date(),
		});
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbModalService.dismiss();
	}
}
